import type { XpEvent } from './xpRules'

export interface XpAwardedDetail {
  userId: string
  awarded: XpEvent
}

const EVENT_NAME = 'xp-awarded'
const target = new EventTarget()

/** Broadcasts a real XP award. Never called on a no-op dedup path (already-
 *  solved problem, already-passed assessment id re-check, etc.) — only when
 *  XP was actually committed. The try/catch below is belt-and-braces: per
 *  the DOM spec, a throwing listener's exception never actually propagates
 *  back to `dispatchEvent`'s caller (it's reported via the global error
 *  handler instead), so this can't realistically break the XP write path —
 *  the catch just makes that guarantee explicit rather than implicit. */
export function notifyXpAwarded(detail: XpAwardedDetail): void {
  try {
    target.dispatchEvent(new CustomEvent<XpAwardedDetail>(EVENT_NAME, { detail }))
  } catch {
    // a subscriber threw — the XP write already succeeded, nothing to do here
  }
}

export function subscribeToXpAwarded(listener: (detail: XpAwardedDetail) => void): () => void {
  const handler = (event: Event) => listener((event as CustomEvent<XpAwardedDetail>).detail)
  target.addEventListener(EVENT_NAME, handler)
  return () => target.removeEventListener(EVENT_NAME, handler)
}
